import * as z from "zod";
import { Id, IsoDate, RunStatus } from "./ids.js";

/** The computer provider refused a request at its own rate limit; the run tells the user to wait. */
export const COMPUTER_RATE_LIMIT_ERROR = "Too many computer requests. Try again later.";

export const RUN_TRIGGERS = [
  "user",
  "routine",
  "resume",
  "follow_up",
  "reaction",
  "call_end",
  "spawn",
  "skill",
  "bot_message",
  "webhook",
  "messaging",
  "cloud_agent",
  "created",
  "tickets",
] as const;
export const RunTriggerSchema = z.enum(RUN_TRIGGERS);
export type RunTrigger = z.infer<typeof RunTriggerSchema>;

/**
 * Whose problem a failed run is. Every kind except "output" is the user's provider account,
 * key, plan or model choice; "output" is a model that returned nothing usable.
 */
export const RUN_ERROR_KINDS = [
  "key",
  "credit",
  "spending",
  "rate",
  "auth",
  "access",
  "model",
  "context",
  "output",
] as const;
export type RunErrorKind = (typeof RUN_ERROR_KINDS)[number];

export const RoutineRunSchema = z.object({
  id: Id,
  botId: Id,
  groupId: Id.nullable(),
  status: RunStatus,
  error: z.string().nullable(),
  createdAt: IsoDate,
  startedAt: IsoDate.nullable(),
  completedAt: IsoDate.nullable(),
  messageId: Id.nullable(),
});
export type RoutineRun = z.infer<typeof RoutineRunSchema>;

export const RoutineRunCursorSchema = z.object({ id: Id, createdAt: IsoDate });
export type RoutineRunCursor = z.infer<typeof RoutineRunCursorSchema>;
/** Runs per history page; the first page a screen shows is shorter. */
export const ROUTINE_HISTORY_PAGE_SIZE = 20;
export const ROUTINE_HISTORY_FIRST_PAGE_SIZE = 5;
export const RoutineHistorySchema = z.object({
  runs: z.array(RoutineRunSchema),
  nextCursor: RoutineRunCursorSchema.nullable(),
});
export type RoutineHistory = z.infer<typeof RoutineHistorySchema>;

export const RunActivityRowSchema = z.object({
  runId: Id,
  botId: Id,
  botName: z.string(),
  groupId: Id.nullable(),
  groupName: z.string().nullable(),
  threadId: Id,
  status: RunStatus,
  trigger: RunTriggerSchema,
  notificationsEnabled: z.boolean(),
  promptSnippet: z.string(),
  agentMessage: z
    .discriminatedUnion("kind", [
      z.strictObject({
        fromBotName: z.string(),
        kind: z.enum(["update", "request"]),
        text: z.string().max(120),
      }),
      z.strictObject({ kind: z.literal("unknown") }),
    ])
    .nullable(),
  updatedAt: z.string(),
});
export type RunActivityRow = z.infer<typeof RunActivityRowSchema>;

export const RunsListOutputSchema = z.object({
  runs: z.array(RunActivityRowSchema),
});
export type RunsListOutput = z.infer<typeof RunsListOutputSchema>;

export const WorkOverviewSchema = z.object({
  runs: z.array(RunActivityRowSchema),
  scheduled: z.array(
    z.object({
      routineId: Id,
      botId: Id,
      botName: z.string(),
      name: z.string(),
      nextRunAt: IsoDate,
    }),
  ),
});
export type WorkOverview = z.infer<typeof WorkOverviewSchema>;

/** A member's own task and public execution activity; never runtime arguments or reasoning. */
export const BotWorkDetailsSchema = z.object({
  botId: Id,
  botName: z.string(),
  hasMore: z.boolean(),
  runs: z.array(
    z.object({
      runId: Id,
      botId: Id,
      groupId: Id.nullable(),
      groupName: z.string().nullable(),
      status: RunStatus,
      trigger: RunTriggerSchema,
      sourceBotName: z.string().nullable(),
      routineName: z.string().nullable(),
      prompt: z.string(),
      promptTruncated: z.boolean(),
      createdAt: IsoDate,
      startedAt: IsoDate.nullable(),
      completedAt: IsoDate.nullable(),
      updatedAt: IsoDate,
      actions: z.array(
        z.object({
          id: Id,
          label: z.string(),
          at: IsoDate,
          outcome: z.enum(["started", "succeeded", "error", "paused"]),
          durationMs: z.number().nonnegative().nullable(),
        }),
      ),
    }),
  ),
});
export type BotWorkDetails = z.infer<typeof BotWorkDetailsSchema>;

/** Durable, bounded diagnostics. Never include database queries, credentials or stack traces. */
export const RunFailureDiagnosticSchema = z.object({
  version: z.literal(1),
  errorType: z.string().max(80),
  code: z.string().max(40).nullable(),
  status: z.number().int().min(100).max(599).nullable(),
  summary: z.string().max(2000),
  provider: z.string().max(200),
  model: z.string().max(200),
  timeoutMs: z.number().nonnegative().nullable(),
  elapsedMs: z.number().nonnegative().nullable(),
});
export type RunFailureDiagnostic = z.infer<typeof RunFailureDiagnosticSchema>;

export const RunFailureDetailsSchema = z.object({
  runId: Id,
  botId: Id,
  botName: z.string(),
  error: z.string().nullable(),
  task: z.string(),
  trigger: RunTriggerSchema,
  createdAt: IsoDate,
  startedAt: IsoDate.nullable(),
  failedAt: IsoDate.nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  diagnostic: RunFailureDiagnosticSchema.nullable(),
  lastAction: z
    .object({
      label: z.string(),
      at: IsoDate,
      outcome: z.string(),
      error: z.string().nullable(),
    })
    .nullable(),
});
export type RunFailureDetails = z.infer<typeof RunFailureDetailsSchema>;
